import { Task } from "../../pojo/Task";
import { EventTaskStateChange, TaskChange, TaskService } from "../../services/task/task.service";
import { TaskUtil } from "./task.util";
import { S25Util } from "../../util/s25-util";
import { ManageWaitlistTaskInfo } from "./manage.tasks.component";
import { Item } from "../../pojo/Item";
import { PreferenceService } from "../../services/preference.service";
import { AccessLevels, Fls } from "../../pojo/Fls";
import { WaitlistEntry, WaitlistReorderWaitingEntry, WaitlistRequest, WaitlistType } from "../../pojo/Waitlist";
import { SpaceService } from "../../services/space.service";
import { OlsService } from "../../services/ols.service";
import { EventMicroService } from "../../services/event.micro.service";
import { ModalService } from "../modal/modal.service";
import { ModalDialogParams } from "../modal/modals/modal.dialog.component";
import { WaitlistStoreType } from "../../stores/waitlist.store";

/**
 * Centralized utility for handling competing/conflicting tasks in waitlist scenarios.
 * Provides a consistent interface for checking, confirming, and denying competing tasks.
 */
export namespace TaskCompetingUtil {
    /**
     * Result of analyzing competing tasks
     */
    export interface CompetingTasksInfo {
        hasCompetingTasks: boolean; // whether there are any competing tasks
        count: number; // number of competing tasks/events
        tasksToNotify: TaskChange[]; // tasks to be denied
        competingEventIds: number[]; // unique event IDs that have competing tasks
    }

    /**
     * Analyzes a single task's competes data to determine what tasks would be affected
     * @param competes - Array of space compete data
     * @returns Information about competing tasks
     */
    export async function analyzeCompetes(competes?: Task.SpaceCompete[]): Promise<CompetingTasksInfo> {
        if (!competes || competes.length === 0) {
            return {
                hasCompetingTasks: false,
                count: 0,
                tasksToNotify: [],
                competingEventIds: [],
            };
        }

        const eventIds = Array.from(new Set(competes.map((c) => c.conflictId)));
        const tasksToNotify = await TaskUtil.deniedCompeteTasks(eventIds, competes);

        return {
            hasCompetingTasks: tasksToNotify.length > 0,
            count: tasksToNotify.length,
            tasksToNotify,
            competingEventIds: eventIds,
        };
    }

    /**
     * Analyzes multiple tasks to find competing tasks
     * Used in batch operations like "Approve Checked"
     * @param tasks - Array of tasks being approved
     * @returns Information about all competing tasks
     */
    export function analyzeMultipleCompetes(tasks: ManageWaitlistTaskInfo[]): {
        allCompetes: Task.SpaceCompete[];
        competingEventIds: number[];
        count: number;
    } {
        const allCompetes = tasks.flatMap((task) => task.competes || []);
        const competingEventIds = Array.from(new Set(allCompetes.map((c) => c.conflictId)));

        return {
            allCompetes,
            competingEventIds,
            count: competingEventIds.length,
        };
    }

    /**
     * Builds a list of TaskChange objects for tasks that should be denied based on competes data
     * @param taskItems - All task items to check
     * @param competes - Space compete data
     * @returns Array of TaskChange objects for matching tasks
     */
    export function buildDeniedTaskChanges(
        taskItems: ManageWaitlistTaskInfo[],
        competes: Task.SpaceCompete[],
    ): TaskChange[] {
        return competes.reduce<TaskChange[]>((acc, compete) => {
            const matchItem = taskItems.find(
                (taskItem) =>
                    taskItem.item.itemId === compete.spaceId &&
                    taskItem.objectType === Item.Ids.Location &&
                    taskItem.reservationId === compete.conflictRsrvId,
            );

            if (matchItem) {
                acc.push({
                    eventId: compete.conflictId,
                    itemId: matchItem.id,
                    itemTypeId: matchItem.item?.itemTypeId,
                    objectType: matchItem.objectType,
                    objectId: matchItem.item?.itemId,
                    itemStateId: matchItem.stateId,
                    isTodo: false,
                });
            }

            return acc;
        }, []);
    }

    export type CompetingAction = "deny" | "skip" | "waitlist";

    /**
     * Approves tasks with optional denial of competing tasks
     * @param tasksToApprove - Tasks to approve
     * @param competingInfo - Information about competing tasks (optional)
     * @param action - What to do with competing tasks: 'deny', 'skip', or 'waitlist'
     * @param waitlistContext - Required when action is 'waitlist' to create waitlist entries
     * @returns Result of the approval operation
     */
    export async function approveWithOptionalDenial(
        tasksToApprove: TaskChange[],
        competingInfo?: CompetingTasksInfo,
        action: CompetingAction = "skip",
        waitlistOptions?: WaitlistAfterDenyOptions,
    ): Promise<{ results: EventTaskStateChange[]; error?: any }> {
        const [approveResp, approveErr] = await S25Util.Maybe(
            TaskService.changeTask(tasksToApprove, Task.States.Assigned),
        );

        if (approveErr) {
            return { results: [], error: approveErr };
        }

        if (action !== "skip" && competingInfo?.hasCompetingTasks && competingInfo.tasksToNotify.length > 0) {
            const [denyResp, denyErr] = await S25Util.Maybe(
                TaskService.changeTask(competingInfo.tasksToNotify, Task.States.Denied),
            );

            if (denyErr) {
                console.warn("Tasks approved but failed to deny competing tasks:", denyErr);
            }

            if (action === "waitlist" && waitlistOptions) {
                const [, waitlistErr] = await S25Util.Maybe(runWaitlistAfterDeny(waitlistOptions));
                if (waitlistErr) {
                    console.warn("Tasks denied but failed to add to waitlist:", waitlistErr);
                }
            }
        }

        return { results: approveResp };
    }

    export interface WaitlistContext {
        waitlistStore: InstanceType<WaitlistStoreType>;
        spaceId: number;
        blockingRsrvId?: number;
        blockingEventId: number;
        competes: Task.SpaceCompete[];
    }

    /**
     * One or more waitlist rows to create after assign + deny (e.g. Approve Checked on manage tasks).
     * Each request must use the newly assigned reservation as blockingRsrvId / blockingEventId,
     * with denied competitors in  WaitlistRequest.waiting.
     */
    export interface BatchWaitlistPayload {
        waitlistStore: InstanceType<WaitlistStoreType>;
        requests: WaitlistRequest[];
    }

    export type WaitlistAfterDenyOptions = WaitlistContext | BatchWaitlistPayload;

    function isBatchWaitlistPayload(options: WaitlistAfterDenyOptions): options is BatchWaitlistPayload {
        return Array.isArray((options as BatchWaitlistPayload).requests);
    }

    async function runWaitlistAfterDeny(options: WaitlistAfterDenyOptions): Promise<void> {
        if (isBatchWaitlistPayload(options)) {
            for (const request of options.requests) {
                await upsertWaitlistAfterLocationAssign(options.waitlistStore, request);
            }
            return;
        }
        await addCompetingToWaitlist(options);
    }

    /**
     * If a persisted waitlist row ( WaitlistEntry.waitlistId}) already exists for this space
     * (assignee was waiting, or blocking unchanged), update it via reorder so blocking event/rsrv stay
     * in sync. Otherwise create a new group.
     */
    export async function upsertWaitlistAfterLocationAssign(
        waitlistStore: InstanceType<WaitlistStoreType>,
        request: WaitlistRequest,
    ): Promise<void> {
        if (!request.blockingRsrvId) {
            if (request.waiting?.length) {
                await waitlistStore.createWaitlistEntriesBatch([request]);
            }
            return;
        }
        if (!request.blockingEventId) {
            await waitlistStore.createWaitlistEntriesBatch([request]);
            return;
        }

        const relatedRsrvIds = [request.blockingRsrvId, ...request.waiting.map((w) => w.rsrvId)];
        const entries = await waitlistStore.getWaitlistEntriesByRsrvIds(relatedRsrvIds, "all_competes", true);

        const existing = findExistingWaitlistGroupForAssign(entries, request);
        if (!existing?.waitlistId) {
            await waitlistStore.createWaitlistEntriesBatch([request]);
            return;
        }

        const reservationIds = mergeReservationOrderForWaitlistUpdate(request, existing);
        const waiting = buildReorderWaitingEntries(reservationIds, request, existing);

        await waitlistStore.reorderWaitlist({
            waitlistId: existing.waitlistId,
            blockingEventId: request.blockingEventId,
            blockingRsrvId: request.blockingRsrvId,
            waiting,
        });
    }

    function findExistingWaitlistGroupForAssign(
        entries: WaitlistEntry[],
        request: WaitlistRequest,
    ): WaitlistEntry | undefined {
        const sameSpace = entries.filter((entry) => entry.spaceId === request.spaceId && entry.waitlistId != null);
        if (!sameSpace.length || !request.blockingRsrvId) return undefined;

        const assigneePromotedFromWaiting = sameSpace.find((entry) =>
            entry.waiting?.some((waiting) => waiting.rsrvId === request.blockingRsrvId),
        );
        if (assigneePromotedFromWaiting) return assigneePromotedFromWaiting;

        return sameSpace.find((entry) => entry.blockingRsrvId === request.blockingRsrvId);
    }

    function mergeReservationOrderForWaitlistUpdate(request: WaitlistRequest, existing: WaitlistEntry): number[] {
        const ordered: number[] = [];
        const appendUnique = (rsrvId: number) => {
            if (!ordered.includes(rsrvId)) ordered.push(rsrvId);
        };

        appendUnique(request.blockingRsrvId!);
        for (const waiting of request.waiting) appendUnique(waiting.rsrvId);
        for (const waiting of existing.waiting ?? []) {
            if (waiting.rsrvId !== request.blockingRsrvId) appendUnique(waiting.rsrvId);
        }

        return ordered;
    }

    function buildReorderWaitingEntries(
        reservationIds: number[],
        request: WaitlistRequest,
        existing: WaitlistEntry,
    ): WaitlistReorderWaitingEntry[] {
        const notifyIdByRsrvId = new Map<number, number>();
        for (const waiting of existing.waiting ?? []) {
            if (waiting.notifyId != null) {
                notifyIdByRsrvId.set(waiting.rsrvId, waiting.notifyId);
            }
        }
        for (const waiting of request.waiting ?? []) {
            if (waiting.notifyId != null) {
                notifyIdByRsrvId.set(waiting.rsrvId, waiting.notifyId);
            }
        }

        return reservationIds
            .filter((rsrvId) => rsrvId !== request.blockingRsrvId)
            .map((rsrvId) => ({
                rsrvId,
                notifyId: notifyIdForReorder(notifyIdByRsrvId.get(rsrvId)),
            }));
    }

    function notifyIdForReorder(notifyId: number | null | undefined): number | null {
        return notifyId != null && notifyId > 0 ? notifyId : null;
    }

    function toWaitlistDateStr(dateStr: string | undefined): string | undefined {
        if (!dateStr) return undefined;
        return S25Util.date.toS25ISODateTimeStr(S25Util.date.dropTZString(dateStr))!;
    }

    /**
     * Builds a waitlist create request: blocking reservation/event holds the space;
     * Task.SpaceCompete - rows supply the waiting reservations (denied competitors).
     */
    export function buildWaitlistRequestForAssignedLocation(
        spaceId: number,
        blockingEventId: number,
        blockingRsrvId: number | undefined,
        competes: Task.SpaceCompete[],
    ): WaitlistRequest | null {
        if (!competes?.length || !blockingRsrvId) return null;

        const waiting = competes.map((c, index) => ({
            rsrvId: c.conflictRsrvId,
            position: index + 1,
            conflictStartDt: toWaitlistDateStr(c.conflictRsrvStartDt ?? c.conflictPreEventDt),
            conflictEndDt: toWaitlistDateStr(c.conflictRsrvEndDt ?? c.conflictPostEventDt),
        }));

        return {
            spaceId,
            blockingRsrvId,
            blockingEventId,
            waiting,
        };
    }

    export function buildWaitlistRequestsForApprovedLocationTasks(
        tasks: ApprovedLocationTaskForWaitlist[],
    ): WaitlistRequest[] {
        const requests: WaitlistRequest[] = [];
        for (const task of tasks) {
            const request = buildWaitlistRequestForAssignedLocation(
                task.spaceId,
                task.eventId,
                task.reservationId,
                task.competes ?? [],
            );
            if (request) requests.push(request);
        }
        return requests;
    }

    export interface ApprovedLocationTaskForWaitlist {
        eventId: number;
        reservationId?: number;
        spaceId: number;
        competes: Task.SpaceCompete[];
    }

    export async function addCompetingToWaitlist(context: WaitlistContext): Promise<void> {
        const request = buildWaitlistRequestForAssignedLocation(
            context.spaceId,
            context.blockingEventId,
            context.blockingRsrvId,
            context.competes,
        );
        if (!request) return;

        await upsertWaitlistAfterLocationAssign(context.waitlistStore, request);
    }

    /**
     * Checks for overlapping waitlist items within a set of tasks being approved
     * Returns task IDs that have time conflicts with each other
     * @param tasks - Tasks being approved that are in waitlist
     * @returns Array of task IDs that overlap with each other
     */
    export function findWaitlistOverlaps(tasks: ManageWaitlistTaskInfo[]): number[] {
        const overlappingIds = new Set<number>();

        for (let i = 0; i < tasks.length; i++) {
            const itemA = tasks[i];
            if (!itemA.competes || itemA.competes.length === 0) continue;

            for (const competeA of itemA.competes) {
                const startA = S25Util.date.toS25ISODateTimeStr(competeA.conflictPreEventDt);
                const endA = S25Util.date.toS25ISODateTimeStr(competeA.conflictPostEventDt);
                if (!startA || !endA) continue;

                for (let j = i + 1; j < tasks.length; j++) {
                    const itemB = tasks[j];
                    if (!itemB.competes || itemB.competes.length === 0) continue;

                    for (const competeB of itemB.competes) {
                        const startB = S25Util.date.toS25ISODateTimeStr(competeB.conflictPreEventDt);
                        const endB = S25Util.date.toS25ISODateTimeStr(competeB.conflictPostEventDt);
                        if (!startB || !endB) continue;

                        const isOverlap = S25Util.date.doRangesOverlap(startA, endA, startB, endB);

                        if (isOverlap) {
                            overlappingIds.add(itemA.id);
                            overlappingIds.add(itemB.id);
                        }
                    }
                }
            }
        }

        return Array.from(overlappingIds);
    }

    /**
     * Generates a user-friendly message for the confirmation dialog
     * @param count - Number of competing tasks
     * @param actionType - Type of action being performed (e.g., "Assign", "Approve")
     * @returns Confirmation message string
     */
    export function getConfirmationMessage(count: number): string {
        if (count === 0) return "";
        const phrase = count === 1 ? "is 1 competing request" : `are ${count} competing requests`;
        return `There ${phrase} for this location`;
    }

    /**
     * After removing a waitlist entry, checks the next entry in line.
     * If the user has OLS edit access (Full) on that event and the entry is not a request (no notifyId),
     * auto-assigns the location to that event.
     */
    export async function assignNextWaitlistEntry(entry: WaitlistEntry, deletedRsrvId: number): Promise<void> {
        const remaining = (entry.waiting ?? [])
            .filter((w) => w.rsrvId !== deletedRsrvId)
            .sort((a, b) => a.position - b.position);

        const next = remaining[0];
        if (!next || next.notifyId) return;

        const ols = await OlsService.getOls([next.eventId], Item.Ids.Event, "edit");
        if (ols?.[0]?.access_level !== "F") return;

        const dialogData = ModalService.dialogType(
            "Yes No",
            {
                message: `The next event on the waitlist is "${next.eventName}". Would you like to assign this location to it?`,
                title: "Assign Location",
            },
            "No",
        );
        await ModalService.modal("dialog", dialogData);
        if (dialogData.answer !== ModalDialogParams.Answer.Yes) return;

        await EventMicroService.putEventReservation(next.eventId, {
            items: [
                {
                    kind: "event",
                    id: next.eventId,
                    profiles: [
                        {
                            reservations: [
                                {
                                    rsrvId: next.rsrvId,
                                    spaces: [{ reserved: [{ spaceId: entry.spaceId }] }],
                                },
                            ],
                        },
                    ],
                },
            ],
        });
    }

    export async function hasWaitlistFls(fls: Fls, locationIds?: number[]): Promise<Map<number, boolean>> {
        const map = new Map<number, boolean>();
        if (fls?.VIEW_OTHERS !== AccessLevels.Full || fls?.SPACE_WAITLIST === AccessLevels.None || S25Util.isInIframe) {
            locationIds?.forEach((id) => map.set(id, false));
            return map;
        }
        if (!locationIds?.length) return map;
        const ols = await OlsService.getOls(locationIds, Item.Ids.Location, "edit");

        const olsById = new Map(ols?.map((o) => [o.object_id, o.access_level]));
        for (const id of locationIds) {
            map.set(id, olsById.get(id) !== AccessLevels.None);
        }
        return map;
    }

    export async function waitlistEnabled(
        objId?: number,
        waitlistType?: WaitlistType,
        globalCheckOnly?: boolean,
    ): Promise<boolean> {
        //TODO  waitlist store/service done in waitlist reorder ticket. will apply here
        const pref = await PreferenceService.getPreferences(["25L_sp_waitlist"]);
        const globalPrefValue = S25Util.toBool(pref?.["25L_sp_waitlist"]?.value);

        if (globalCheckOnly) return globalPrefValue;

        if (!waitlistType && objId) {
            const objDetail = await SpaceService.getSpaceMinimal(objId);
            waitlistType = objDetail?.waitlist ?? null;
        }

        return globalPrefValue && !!waitlistType;
    }

    export async function waitlistEnabledBatch(objIds: number[]): Promise<Map<number, boolean>> {
        const map = new Map<number, boolean>();
        if (!objIds?.length) return map;

        const pref = await PreferenceService.getPreferences(["25L_sp_waitlist"]);
        const globalPrefValue = S25Util.toBool(pref?.["25L_sp_waitlist"]?.value);

        if (!globalPrefValue) {
            for (const id of objIds) map.set(id, false);
            return map;
        }

        const uniqueIds = Array.from(new Set(objIds.filter((id) => id > 0)));
        const spaces = await SpaceService.getSpacesMinimal(uniqueIds);
        const spaceById = new Map(spaces.map((s) => [S25Util.parseInt(s.space_id), s]));

        for (const id of uniqueIds) {
            const waitlistType = spaceById.get(id)?.waitlist ?? null;
            map.set(id, !!waitlistType);
        }

        return map;
    }
}
